import classNames from 'classnames';
import { useCallback, useRef, useState, useEffect, useMemo } from 'react';

import style from './style.module.css';

const SilenceBlock = ({ videoDuration, start, enabled = true, duration, containerWidth, onEdit = () => {}, otherBlocks = [], marginStart = 0 }) => {
  const blockRef = useRef();
  const [direction, setDirection] = useState();

  const left = useMemo(() => (start * containerWidth) / videoDuration, [start, containerWidth, videoDuration]);
  const width = useMemo(() => (duration * containerWidth) / videoDuration, [duration, containerWidth, videoDuration]);
  const extendable = useMemo(() => enabled && width >= 12, [enabled, width]);

  const onMouseDown = useCallback(
    (e) => {
      if (blockRef.current && extendable) {
        const blockWidth = blockRef.current.getBoundingClientRect().width;
        const container = blockRef.current.parentNode;
        if (e.nativeEvent.offsetX >= 0 && e.nativeEvent.offsetX <= 6) {
          setDirection('left');
          container.style.cursor = 'ew-resize';
        } else if (e.nativeEvent.offsetX <= blockWidth && e.nativeEvent.offsetX >= blockWidth - 6) {
          setDirection('right');
          container.style.cursor = 'ew-resize';
        }
      }
    },
    [setDirection, extendable]
  );

  const onMouseMove = useCallback(
    (e) => {
      const container = blockRef.current?.parentNode;
      if (container) {
        if (direction === 'left') {
          const previousBlock = otherBlocks.reduce((acc, block) => {
            if (block.start < start) {
              if (!acc) {
                return block;
              } else {
                return block.start > acc.start ? block : acc;
              }
            } else {
              return acc;
            }
          }, null);

          const min = previousBlock ? ((previousBlock.start + previousBlock.duration) * containerWidth) / videoDuration : marginStart;

          const pos = Math.max(e.x - container.getBoundingClientRect().x + marginStart, min);
          const time = (pos * videoDuration) / containerWidth;
          if (pos < left + width - 12) {
            onEdit({ start: time, duration: duration + (start - time) });
          }
        } else if (direction === 'right') {
          const nextBlock = otherBlocks.reduce((acc, block) => {
            if (block.start > start) {
              if (!acc) {
                return block;
              } else {
                return block.start < acc.start ? block : acc;
              }
            } else {
              return acc;
            }
          }, null);

          const max = nextBlock ? (nextBlock.start * containerWidth) / videoDuration : containerWidth + marginStart;

          const pos = Math.min(e.x - container.getBoundingClientRect().x + marginStart, max);
          const time = (pos * videoDuration) / containerWidth;
          if (pos > left + 12) {
            onEdit({ duration: time - start });
          }
        }
      }
    },
    [direction, containerWidth, duration, left, onEdit, otherBlocks, start, videoDuration, width, marginStart]
  );

  const onMouseUp = useCallback(
    (e) => {
      if (blockRef.current) {
        const container = blockRef.current.parentNode;
        container.style.cursor = '';
        if (e.target === blockRef.current && !direction) {
          onEdit({ enabled: !enabled });
        }
      }
      setDirection(null);
    },
    [setDirection, direction, onEdit, enabled]
  );

  useEffect(() => {
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);

    // cleanup this component
    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [onMouseMove, onMouseUp]);

  return (
    <div
      className={classNames(style.silence, { [style.extendable]: extendable, [style.disabled]: !enabled })}
      style={{
        left: left - marginStart,
        width,
      }}
      onMouseDown={onMouseDown}
      ref={blockRef}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

export default SilenceBlock;
